import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getCurrentLanguage, getLanguageFromPath, validateLanguageCode } from '../utils/languageUtils';
import i18n from '../i18n';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const location = useLocation();

  // Initialize language from URL, localStorage, or browser
  useEffect(() => {
    const detectedLang = getCurrentLanguage(location.pathname);
    const validLang = validateLanguageCode(detectedLang);
    setCurrentLanguage(validLang);
    
    // Update browser language for the translation pipeline
    if (typeof window !== 'undefined') {
      document.documentElement.lang = validLang;
    }

    if (i18n && i18n.changeLanguage) {
      i18n.changeLanguage(validLang);
    }
  }, [location.pathname]);

  // Normalize URL to include language prefix when language is not default
  useEffect(() => {
    const urlLang = getLanguageFromPath(location.pathname);
    if (!urlLang && currentLanguage && currentLanguage !== 'en') {
      const basePath = location.pathname.startsWith('/') ? location.pathname : `/${location.pathname}`;
      const newPath = `/${currentLanguage}${basePath}`;
      window.history.replaceState({}, '', newPath);
    }
  }, [location.pathname, currentLanguage]);

  // Listen for language changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'selectedLanguage' && e.newValue) {
        setCurrentLanguage(e.newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const changeLanguage = (langCode) => {
    const validLang = validateLanguageCode(langCode);
    setCurrentLanguage(validLang);
    localStorage.setItem('selectedLanguage', validLang);
    
    // Update browser language
    if (typeof window !== 'undefined') {
      document.documentElement.lang = validLang;
    }

    if (i18n && i18n.changeLanguage) {
      i18n.changeLanguage(validLang);
    }
  };

  return (
    <LanguageContext.Provider value={{ currentLanguage, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};
